import type { EpgDateEx } from '~/entities/epg/dates'
import type { EpgTimeLine } from './index.h'

import { D } from '@mobily/ts-belt'
import { createEvent, createStore, merge, sample } from 'effector'
import { epgDatesModel, isToday, toTimestamp } from '~/entities/epg/dates'
import { selectEpgDateModel } from '~/features/epg/dates/select'
import { model as clock } from '~/shared/clock'
import { ONE_DAY_MS } from '~/shared/constants/time'
import { lookbehind } from '~/shared/effector'
import { noop } from '~/shared/helpers'
import { DATE_FORMATS, formatStartTime } from '~/shared/helpers/date'
import {
  createNewTimeline,
  getTimelineStart,
  updateExistingTimeline,
} from './lib'

// init timeline
export const init = createEvent()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

export const setStart = createEvent<number | null | undefined | void>()
export const updateStart = setStart.prepend<{ to: number }>(D.getUnsafe('to')) // `setStart` from ui
export const $start = createStore(NaN) //
  .on(setStart, (_, to) => getTimelineStart(to ?? undefined))

export const setTimeline = createEvent<number | null | undefined | void>()
export const updateTimeline = createEvent<Partial<EpgTimeLine>>()
export const $timeline = createStore<EpgTimeLine>(createNewTimeline(Date.now()))
  .on(setTimeline, (_, to) => createNewTimeline(to ?? undefined))
  .on(updateTimeline, updateExistingTimeline)

export const $duration = $timeline.map(({ duration }) => duration)

// pipe to format timeline time
export const $formatTimePipe = createStore((time: string | Date | number) =>
  formatStartTime(time, DATE_FORMATS.HH_MM_LOCALIZED)
)

// on each minute update timeline it might shift visible period on `scale` value
// filter: only if it is next scale point, otherwise - means user clicked next period (no need period update)
sample({
  clock: clock.$minutes,
  source: { timeline: $timeline, start: $start },
  filter: ({ timeline, start }, now) =>
    getTimelineStart(now) === start + timeline.scale,
  fn: (_, now) => now,
  target: setStart,
})

// event, which fires when user selects a day manually, but not via timeline shifts
// this is required to avoid infinite loop of updates day -> timeline -> day -> timeline -> ...
const daySelected = createEvent<EpgDateEx | null>()
lookbehind({
  source: selectEpgDateModel.$active,
  clear: merge([updateStart, init]),
  within: 0, // `updateStart` and `$active` are updated in the same tick, so no need for long timeout here
  target: daySelected,
})

// on init, and on any day change -> set new timeline start
sample({
  clock: init,
  source: {
    active: selectEpgDateModel.$active,
    start: $start,
  },
  filter: ({ active, start }) => active != null && isNaN(start), // only if start is not set

  fn: ({ active }) => (isToday(active!) ? Date.now() : toTimestamp(active!)),
  target: setStart,
})

// on init, and on any day change -> set new timeline start
sample({
  clock: daySelected,
  source: {
    active: selectEpgDateModel.$active,
  },
  filter: ({ active }) => active != null,

  fn: ({ active }) => (isToday(active!) ? Date.now() : toTimestamp(active!)),
  target: setStart,
})

// on init, and on any day change -> set new timeline
sample({
  clock: [init, daySelected],
  source: {
    active: selectEpgDateModel.$active,
  },
  filter: ({ active }) => active != null && isToday(active),
  fn: noop,
  target: setTimeline,
})

// update (from ui) selected day in day list, so it syncs with timeline
sample({
  clock: updateStart,
  source: {
    startTime: $start,
    week: epgDatesModel.$week,
  },
  fn: ({ startTime, week }) => {
    const day = week.find((date) => {
      const midnight = toTimestamp(date)
      return midnight <= startTime && startTime < midnight + ONE_DAY_MS
    })
    return day ?? null
  },
  target: selectEpgDateModel.setActive,
})

//
// left and reset logic
//

sample({
  clock: reset,
  target: [$start.reinit],
})
